import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { palette, spacing, typography } from '../../../style-utils/theme';
import * as api from '../../../api/admin-client';
import { useAdminList, useAdminResource } from './use-admin';
import {
  AdminTable,
  Column,
  Id,
  Tag,
  Tone,
  StatusTag,
  labelFor,
  toneFor,
  when,
  num,
  zoneCell,
} from './admin-table';
import { formatDayKey, isDayKey, zoneLabel } from '../../../function-utils/building-time';
import {
  Card,
  CardHead,
  CardTitle,
  CardBody,
  Tile,
  TileGrid,
  TileLabel,
  TileValue,
  TileFoot,
  Toolbar,
  Select,
  SearchBox,
  Btn,
  Empty,
  ErrorBox,
  NoteBox,
  DefList,
  Split,
  Bar,
  Mono,
  Scroller,
  SkeletonRow,
} from './admin.styled';
import { IntegrationsIndex } from './admin-integrations';
import toast from 'react-hot-toast';
import { ConfirmSpec } from './elevate-dialog';

/**
 * EVERY SCREEN IN THE CONSOLE.
 *
 * ── ONE FILE, AND WHY THAT IS THE RIGHT CALL HERE ──────────────────────────
 *
 * Each screen is a column spec plus a filter bar. Split across twenty-six files
 * they would be twenty-six copies of the same fifteen lines, and the copies
 * would drift — which is the exact history of the /dashboard tables that WP-37
 * had to undo. What is genuinely per-screen (which columns, which filters,
 * which links) lives here; everything else is in `AdminTable` and `useAdminList`.
 *
 * ── FILTERS ARE SERVER PARAMETERS, ALWAYS ──────────────────────────────────
 *
 * No screen filters or sorts an array in the browser. A client-side filter over
 * a fifty-row page is a filter over page one that lies about the rest, and at
 * the scale this console is written for that is not a nuance.
 *
 * ── SOURCES ARE PRINTED ────────────────────────────────────────────────────
 *
 * The overview renders the `sources` map the API sends with it, so every number
 * on the busiest screen can be traced to the column it came from without
 * reading the backend.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

const n = (v: unknown) => num(v);

/* A 'YYYY-MM' month key as a word ("October"); "this month" when the server
   sent none (an older answer). The key is on the READER's calendar. */
const monthLabel = (key?: string | null): string =>
  key && /^\d{4}-\d{2}$/.test(key)
    ? formatDayKey(`${key}-01`, { month: 'long' })
    : 'this month';

/* A day KEY from the server ('YYYY-MM-DD', in the answer's `tz`) as a date.
   Printed as it is, never re-zoned; an older answer's instant is cut to its
   date part, which is the key it carried. */
const dayLabel = (value: unknown): string => {
  const key = String(value ?? '').slice(0, 10);

  return isDayKey(key)
    ? formatDayKey(key, { day: 'numeric', month: 'short', year: 'numeric' })
    : key;
};

const Money: React.FC<{ cents: number }> = ({ cents }) => (
  <>{(cents / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' })}</>
);

/* Micro-dollars: the AI ledger's unit. 1_000_000 micros = 1 unit of currency. */
const Micros: React.FC<{ micros: number }> = ({ micros }) => (
  <>
    {(micros / 1_000_000).toLocaleString(undefined, {
      style: 'currency',
      currency: 'USD',
    })}
  </>
);

const bytes = (v: unknown): string => {
  const b = Number(v || 0);
  if (!b) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(b) / Math.log(1024)));

  return `${(b / 1024 ** i).toFixed(i ? 1 : 0)} ${units[i]}`;
};

/* ═══════════════════════════════════════════════════════════════════════════
   OVERVIEW
   ═══════════════════════════════════════════════════════════════════════════ */

export const OverviewScreen: React.FC = () => {
  const { data, loading, error } = useAdminResource<api.Overview>('/overview');
  const nav = useNavigate();

  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (loading || !data) {
    return (
      <div aria-busy="true">
        {Array.from({ length: 8 }).map((_, i) => (
          /* eslint-disable-next-line react/no-array-index-key */
          <SkeletonRow key={i} />
        ))}
      </div>
    );
  }

  const c = data.counts;
  const w = data.warnings as Record<string, number>;

  const warnings = [
    ['Past due', w.past_due, 'subscriptions?status=past_due'],
    ['Unpaid', w.unpaid, 'subscriptions?status=unpaid'],
    ['Trials ending in 7 days', w.trials_ending_7d, 'subscriptions?status=trialing'],
    ['Trials already over', w.trials_overdue, 'subscriptions?status=trialing'],
    ['Unprocessed Stripe events', w.stripe_unprocessed, 'stripe?processed=false'],
    ['Organizations not active', w.orgs_not_active, 'organizations'],
    ['Shifts open over 10h', w.stale_shifts, 'shifts?open=true'],
  ].filter(([, v]) => Number(v) > 0) as [string, number, string][];

  return (
    <>
      {warnings.length ? (
        <Card>
          <CardHead>
            <CardTitle>Needs attention</CardTitle>
          </CardHead>
          <CardBody>
            <TileGrid>
              {warnings.map(([label, value, href]) => (
                <Tile key={label} tone={value > 0 ? 'warning' : undefined}>
                  <TileLabel>{label}</TileLabel>
                  <TileValue>{n(value)}</TileValue>
                  <TileFoot>
                    <Btn onClick={() => nav(`/admin/${href}`)}>Open</Btn>
                  </TileFoot>
                </Tile>
              ))}
            </TileGrid>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHead>
          <CardTitle>Platform</CardTitle>
        </CardHead>
        <CardBody>
          <TileGrid>
            <Tile>
              <TileLabel>Organizations</TileLabel>
              <TileValue>{n(c.orgs_total)}</TileValue>
              <TileFoot>
                {n(c.orgs_active)} active · {n(c.orgs_suspended)} suspended
              </TileFoot>
            </Tile>
            <Tile>
              <TileLabel>People</TileLabel>
              <TileValue>{n(c.users_total)}</TileValue>
              <TileFoot>
                {n(c.users_active)} active · {n(c.users_inactive)} inactive
              </TileFoot>
            </Tile>
            <Tile>
              {/* TWO DIFFERENT NUMBERS, BOTH NAMED. The value counts new
                  ORGANIZATIONS; the line under it counts every new user row,
                  residents and staff included. Unlabelled, the tile read as
                  one metric contradicting itself ("2" over "30 new accounts"),
                  and QA filed it as a broken interpolation. */}
              <TileLabel>New organizations (30 days)</TileLabel>
              <TileValue>{n(c.signups_30d)}</TileValue>
              <TileFoot>{n(c.users_30d)} new accounts of any kind</TileFoot>
            </Tile>
            <Tile>
              <TileLabel>Staff</TileLabel>
              <TileValue>
                {n(
                  Number(c.users_owner) +
                    Number(c.users_manager) +
                    Number(c.users_concierge)
                )}
              </TileValue>
              <TileFoot>
                {n(c.users_owner)} owner · {n(c.users_manager)} mgr ·{' '}
                {n(c.users_concierge)} concierge
              </TileFoot>
            </Tile>
            <Tile>
              <TileLabel>Portfolios</TileLabel>
              <TileValue>{n(c.portfolios_total)}</TileValue>
            </Tile>
            <Tile>
              <TileLabel>Buildings</TileLabel>
              <TileValue>{n(c.buildings_total)}</TileValue>
              <TileFoot>{n(c.buildings_active)} active</TileFoot>
            </Tile>
            <Tile>
              <TileLabel>Units</TileLabel>
              <TileValue>{n(c.units_total)}</TileValue>
              <TileFoot>
                {n(c.units_active)} active · {n(c.units_archived)} returned
              </TileFoot>
            </Tile>
            <Tile>
              <TileLabel>Occupied units</TileLabel>
              <TileValue>{n(c.units_occupied)}</TileValue>
              <TileFoot>{n(c.residents_open)} open residencies</TileFoot>
            </Tile>
          </TileGrid>
        </CardBody>
      </Card>

      <Split>
        <Card>
          <CardHead>
            <CardTitle>Subscriptions</CardTitle>
          </CardHead>
          <CardBody>
            <Scroller>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th
                      style={{
                        textAlign: 'left',
                        color: palette.slate,
                        paddingBottom: 6,
                      }}
                    >
                      Status
                    </th>
                    <th style={{ textAlign: 'right', color: palette.slate }}>Count</th>
                    <th style={{ textAlign: 'right', color: palette.slate }}>Units</th>
                  </tr>
                </thead>
                <tbody>
                  {data.subscriptions.map(s => (
                    <tr key={s.status}>
                      <td style={{ padding: '4px 0' }}>
                        <StatusTag value={s.status} />
                      </td>
                      <td style={{ textAlign: 'right' }}>{n(s.n)}</td>
                      <td style={{ textAlign: 'right' }}>{n(s.units)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Scroller>
          </CardBody>
        </Card>

        <Card>
          <CardHead>
            <CardTitle>Money & AI</CardTitle>
          </CardHead>
          <CardBody>
            <DefList>
              <dt>Recurring (mirror)</dt>
              <dd>
                <Money cents={data.revenue.mrr_cents_mirror} />
              </dd>
              <dt>{`IQ spend, ${monthLabel(data.iq.month)} to date`}</dt>
              <dd>
                <Micros micros={data.iq.spend_micros_mtd} />
              </dd>
              <dt>{`IQ calls, ${monthLabel(data.iq.month)} to date`}</dt>
              <dd>
                {n(data.iq.calls_mtd)}
                {data.iq.errors_mtd ? ` · ${n(data.iq.errors_mtd)} errors` : ''}
              </dd>
            </DefList>
            <div style={{ marginTop: spacing.sm }}>
              <NoteBox>{data.revenue.note}</NoteBox>
            </div>
          </CardBody>
        </Card>
      </Split>

      {data.over_allowance.length ? (
        <Card>
          <CardHead>
            <CardTitle>Organizations over their unit allowance</CardTitle>
          </CardHead>
          <CardBody>
            <AdminTable<Row>
              rows={data.over_allowance}
              rowKey={r => String(r.id)}
              onRowClick={r => nav(`/admin/organizations/${encodeURIComponent(r.id)}`)}
              columns={[
                { key: 'name', header: 'Organization', render: r => r.name || r.id },
                { key: 'used', header: 'Used', numeric: true, render: r => n(r.used) },
                {
                  key: 'bought',
                  header: 'Bought',
                  numeric: true,
                  render: r => n(r.bought),
                },
                {
                  key: 'over',
                  header: 'Over by',
                  numeric: true,
                  render: r => (
                    <strong style={{ color: palette.alert }}>
                      {n(r.used - r.bought)}
                    </strong>
                  ),
                },
              ]}
            />
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHead>
          <CardTitle>Where these numbers come from</CardTitle>
        </CardHead>
        <CardBody>
          <DefList>
            {Object.entries(data.sources).map(([k, v]) => (
              <React.Fragment key={k}>
                <dt>{k}</dt>
                <dd>
                  <Mono>{v}</Mono>
                </dd>
              </React.Fragment>
            ))}
          </DefList>
        </CardBody>
      </Card>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   A GENERIC LIST SCREEN
   ═══════════════════════════════════════════════════════════════════════════ */

interface FilterSpec {
  name: string;
  label: string;
  options: { value: string; label: string }[];
}

interface ListScreenProps {
  resource: string;
  columns: Column<Row>[];
  rowKey?: (r: Row) => string;
  filters?: FilterSpec[];
  searchable?: boolean;
  searchParam?: string;
  searchPlaceholder?: string;
  onRowClick?: (r: Row, nav: ReturnType<typeof useNavigate>) => void;
  emptyLabel?: string;
  note?: React.ReactNode;
  extraParams?: Record<string, string>;
}

export const ListScreen: React.FC<ListScreenProps> = ({
  resource,
  columns,
  rowKey = r => String(r.id),
  filters = [],
  searchable,
  searchParam = 'q',
  searchPlaceholder = 'Filter…',
  onRowClick,
  emptyLabel,
  note,
  extraParams = {},
}) => {
  const nav = useNavigate();
  const [term, setTerm] = useState('');

  // FILTER STATE IS HYDRATED FROM THE URL, NOT FROM `{}`.
  //
  // The overview's "Shifts open over 10h" tile links to `shifts?open=true`.
  // Holding the selection in component state alone meant that link landed on
  // an unfiltered list showing ended shifts, with the State control reading
  // "All" — the operator followed an alert about open shifts and was shown
  // everything. Every list screen took the same link and ignored it, so the
  // fix belongs here rather than on the shifts route.
  //
  // Only the names this screen declares are read, so an unrelated parameter
  // in the URL cannot become a filter.
  const [urlParams, setUrlParams] = useSearchParams();
  const filterNames = filters.map(f => f.name).join(',');
  const picked = useMemo(() => {
    const out: Record<string, string> = {};
    filterNames
      .split(',')
      .filter(Boolean)
      .forEach(name => {
        const v = urlParams.get(name);
        if (v) out[name] = v;
      });

    return out;
  }, [urlParams, filterNames]);

  const pick = (name: string, value: string) => {
    const next = new URLSearchParams(urlParams);
    if (value) next.set(name, value);
    else next.delete(name);
    setUrlParams(next, { replace: true });
  };

  // The dependency list is the SERIALISED filters, not the objects. Every
  // caller rebuilds `extraParams` and `picked` on each render, so depending on
  // the objects themselves would make this memo change identity every time and
  // refire the request on every keystroke of unrelated state.
  const extraKey = JSON.stringify(extraParams);
  const pickedKey = JSON.stringify(picked);
  const params = useMemo(
    () => ({
      ...(JSON.parse(extraKey) as Record<string, string>),
      ...(JSON.parse(pickedKey) as Record<string, string>),
      ...(searchable && term ? { [searchParam]: term } : {}),
    }),
    [extraKey, pickedKey, term, searchable, searchParam]
  );

  const state = useAdminList<Row>(resource, params, { debounce: !!searchable });

  return (
    <Card>
      <CardHead>
        <Toolbar>
          {searchable ? (
            <SearchBox
              value={term}
              onChange={e => setTerm(e.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
            />
          ) : null}
          {filters.map(f => (
            <label key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: typography.size.xxs, color: palette.slate }}>
                {f.label}
              </span>
              <Select
                value={picked[f.name] ?? ''}
                onChange={e => pick(f.name, e.target.value)}
              >
                <option value="">All</option>
                {f.options.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </label>
          ))}
        </Toolbar>
        <Btn onClick={state.reload}>Refresh</Btn>
      </CardHead>
      <CardBody>
        {note ? <div style={{ marginBottom: spacing.sm }}>{note}</div> : null}
        <AdminTable<Row>
          columns={columns}
          rows={state.items}
          rowKey={rowKey}
          loading={state.loading}
          loadingMore={state.loadingMore}
          error={state.error}
          total={state.total}
          hasMore={state.hasMore}
          onLoadMore={state.loadMore}
          emptyLabel={emptyLabel}
          onRowClick={onRowClick ? r => onRowClick(r, nav) : undefined}
        />
      </CardBody>
    </Card>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   THE COLUMN SPECS
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Independent Stays IS1: an organisation's products as one pill — "Desk",
 * "Desk + Stays" or "Stays". Missing values read as the server's default
 * (Desk on), so a row from an older API still says Desk.
 */
export const productsLabel = (desk?: boolean | null, stays?: boolean | null): { text: string; tone: Tone } => {
  const d = desk !== false;
  if (d && stays) return { text: 'Desk + Stays', tone: 'brand' };
  if (d) return { text: 'Desk', tone: 'neutral' };

  return { text: 'Stays', tone: 'success' };
};

export const ORG_COLUMNS: Column<Row>[] = [
  { key: 'name', header: 'Organization', render: r => r.name || <Id value={r.id} /> },
  { key: 'owner', header: 'Owner email', wide: true, render: r => r.owner_email || '—' },
  { key: 'status', header: 'Status', render: r => <StatusTag value={r.status} /> },
  {
    key: 'products',
    header: 'Products',
    render: r => {
      const p = productsLabel(r.desk_enabled, r.stays_on);

      return <Tag tone={p.tone}>{p.text}</Tag>;
    },
  },
  {
    key: 'sub',
    header: 'Subscription',
    render: r =>
      r.sub_status ? (
        // The plan NAME first, and the code only when there is no name: this
        // cell is where `desk_iq_monthly_v1` used to reach the operator.
        <Tag tone={toneFor(r.sub_status)}>
          {r.plan_name || labelFor(r.plan_code) || 'Unknown plan'} ·{' '}
          {labelFor(r.sub_status)}
        </Tag>
      ) : (
        <span style={{ color: palette.slateLight }}>none</span>
      ),
  },
  { key: 'users', header: 'People', numeric: true, render: r => n(r.users_count) },
  {
    key: 'buildings',
    header: 'Buildings',
    numeric: true,
    render: r => n(r.buildings_count),
  },
  {
    key: 'units',
    header: 'Units',
    numeric: true,
    render: r => (
      <span title={`${r.units_billable} billable of ${r.unit_quantity ?? 0} bought`}>
        {n(r.units_billable)}
        <span style={{ color: palette.slateLight }}> / {n(r.unit_quantity ?? 0)}</span>
      </span>
    ),
  },
  {
    key: 'residents',
    header: 'Residents',
    numeric: true,
    wide: true,
    render: r => n(r.residents_count),
  },
  { key: 'created', header: 'Created', wide: true, render: r => when(r.createdAt) },
];

export const WAITLIST_COLUMNS: Column<Row>[] = [
  // The address is the reason this screen exists, so it leads. It arrives
  // already decrypted: stays_waitlist stores AES-GCM ciphertext and the query
  // decrypts it for display (see Q.listWaitlist).
  { key: 'email', header: 'Email', render: r => r.email || <Id value={r.id} /> },
  {
    key: 'product',
    header: 'Product',
    render: r => <Tag tone="brand">{String(r.product || '—')}</Tag>,
  },
  {
    key: 'role',
    header: 'They said they are',
    wide: true,
    render: r => r.role || <span style={{ color: palette.slateLight }}>not said</span>,
  },
  { key: 'status', header: 'Status', render: r => <StatusTag value={r.status} /> },
  {
    key: 'notified',
    header: 'Notified',
    wide: true,
    // notified_at is stamped only once the hello@lobbero.com email actually
    // sent. An empty column is the useful signal: the row is filed, and the
    // notification did not go out.
    render: r =>
      r.notified_at ? (
        when(r.notified_at)
      ) : (
        <span style={{ color: palette.slateLight }}>no email sent</span>
      ),
  },
  { key: 'source', header: 'Came from', wide: true, render: r => r.source || '—' },
  { key: 'created', header: 'Joined', render: r => when(r.created_at) },
];

export const USER_COLUMNS: Column<Row>[] = [
  { key: 'name', header: 'Name', render: r => r.full_name || <Id value={r.id} /> },
  { key: 'email', header: 'Email', render: r => r.email },
  {
    key: 'role',
    header: 'Role',
    render: r =>
      r.role ? (
        <Tag tone="brand">{r.role.replace('_', ' ')}</Tag>
      ) : r.open_occupancies ? (
        <Tag tone="neutral">resident</Tag>
      ) : (
        <span style={{ color: palette.slateLight }}>none</span>
      ),
  },
  { key: 'status', header: 'Status', render: r => <StatusTag value={r.status} /> },
  {
    key: 'org',
    header: 'Organization',
    wide: true,
    render: r => r.org_name || <Id value={r.organization_id} />,
  },
  {
    key: 'verified',
    header: 'Verified',
    wide: true,
    render: r =>
      r.email_verified_at ? when(r.email_verified_at) : <Tag tone="warning">no</Tag>,
  },
  { key: 'created', header: 'Joined', wide: true, render: r => when(r.createdAt) },
];

export const BUILDING_COLUMNS: Column<Row>[] = [
  {
    key: 'name',
    header: 'Building',
    render: r => r.property_name || <Id value={r.id} />,
  },
  {
    key: 'address',
    header: 'Address',
    wide: true,
    render: r => [r.address, r.city, r.country].filter(Boolean).join(', ') || '—',
  },
  { key: 'city', header: 'City', render: r => r.city || '—' },
  {
    key: 'org',
    header: 'Organization',
    render: r => r.org_name || <Id value={r.organization_id} />,
  },
  {
    key: 'portfolio',
    header: 'Portfolio',
    wide: true,
    render: r => r.portfolio_name || '—',
  },
  { key: 'status', header: 'Status', render: r => <StatusTag value={r.status} /> },
  { key: 'units', header: 'Units', numeric: true, render: r => n(r.units_total) },
  { key: 'residents', header: 'Residents', numeric: true, render: r => n(r.residents) },
  {
    key: 'tz',
    header: 'Building time zone',
    wide: true,
    render: r => <Mono>{zoneCell(r.timezone)}</Mono>,
  },
];

export const UNIT_COLUMNS: Column<Row>[] = [
  { key: 'unit', header: 'Unit', render: r => r.unit_number },
  {
    key: 'building',
    header: 'Building',
    render: r => r.property_name || <Id value={r.building_id} />,
  },
  {
    key: 'org',
    header: 'Organization',
    wide: true,
    render: r => r.org_name || <Id value={r.organization_id} />,
  },
  { key: 'floor', header: 'Floor', wide: true, render: r => r.floor || '—' },
  { key: 'kind', header: 'Kind', wide: true, render: r => r.kind || '—' },
  { key: 'status', header: 'Status', render: r => <StatusTag value={r.status} /> },
  {
    key: 'occ',
    header: 'Occupants',
    numeric: true,
    render: r =>
      Number(r.occupants) ? (
        n(r.occupants)
      ) : (
        <span style={{ color: palette.slateLight }}>vacant</span>
      ),
  },
  {
    key: 'sqft',
    header: 'Size',
    numeric: true,
    wide: true,
    render: r => (r.sq_ft ? n(r.sq_ft) : '—'),
  },
];

export const RESIDENT_COLUMNS: Column<Row>[] = [
  {
    key: 'name',
    header: 'Resident',
    render: r =>
      [r.first_name, r.last_name].filter(Boolean).join(' ') || <Id value={r.id} />,
  },
  { key: 'unit', header: 'Unit', render: r => r.unit_number || '—' },
  { key: 'building', header: 'Building', render: r => r.property_name || '—' },
  { key: 'org', header: 'Organization', wide: true, render: r => r.org_name || '—' },
  {
    key: 'state',
    header: 'Tenancy',
    render: r =>
      r.moved_out ? (
        <Tag tone="neutral">moved out</Tag>
      ) : (
        <Tag tone="success">current</Tag>
      ),
  },
  {
    key: 'in',
    header: 'Moved in',
    wide: true,
    render: r => (r.moved_in ? String(r.moved_in).slice(0, 10) : '—'),
  },
  {
    key: 'account',
    header: 'Account',
    render: r =>
      r.user_id ? (
        <StatusTag value={r.user_status || 'linked'} />
      ) : (
        <span style={{ color: palette.slateLight }}>no login</span>
      ),
  },
];

export const SUBSCRIPTION_COLUMNS: Column<Row>[] = [
  { key: 'owner', header: 'Owner email', wide: true, render: r => r.owner_email || '—' },
  {
    key: 'org',
    header: 'Organization',
    render: r => r.org_name || <Id value={r.organization_id} />,
  },
  { key: 'plan', header: 'Plan', render: r => r.plan_code || r.plan_name || '—' },
  { key: 'status', header: 'Status', render: r => <StatusTag value={r.status} /> },
  {
    key: 'units',
    header: 'Units',
    numeric: true,
    render: r => (
      <span title={`${r.units_used} used of ${r.unit_quantity ?? 0} bought`}>
        {n(r.units_used)}
        <span style={{ color: palette.slateLight }}> / {n(r.unit_quantity ?? 0)}</span>
      </span>
    ),
  },
  {
    key: 'renews',
    header: 'Renews',
    wide: true,
    // No Stripe subscription means nothing ever advances current_period_end
    // (reconcile classes it NO_STRIPE_OBJECT), so the date is a frozen seed
    // value, not a renewal. Say so instead of showing it.
    render: r =>
      !r.stripe_subscription_id ? (
        <Tag tone="neutral">not billed · no Stripe</Tag>
      ) : r.cancel_at_period_end ? (
        <Tag tone="warning">cancels {when(r.current_period_end)}</Tag>
      ) : (
        when(r.current_period_end)
      ),
  },
  { key: 'trial', header: 'Trial ends', wide: true, render: r => when(r.trial_end) },
  {
    key: 'stripe',
    header: 'Stripe',
    wide: true,
    render: r => <Id value={r.stripe_subscription_id} />,
  },
];

export const INVITATION_COLUMNS: Column<Row>[] = [
  { key: 'email', header: 'Invitee', render: r => r.email },
  {
    key: 'role',
    header: 'Role',
    render: r => <Tag tone="brand">{String(r.role || '').replace('_', ' ')}</Tag>,
  },
  {
    key: 'status',
    header: 'Status',
    render: r =>
      r.expired ? <Tag tone="alert">Expired</Tag> : <StatusTag value={r.status} />,
  },
  { key: 'org', header: 'Organization', wide: true, render: r => r.org_name || '—' },
  {
    key: 'building',
    header: 'Building',
    wide: true,
    render: r => r.property_name || '—',
  },
  { key: 'sent', header: 'Sent', render: r => when(r.last_sent_at || r.createdAt) },
  {
    key: 'resends',
    header: 'Resends',
    numeric: true,
    wide: true,
    render: r => n(r.resend_count),
  },
  { key: 'accepted', header: 'Accepted', render: r => when(r.accepted_at) },
];

export const LOG_COLUMNS: Column<Row>[] = [
  { key: 'when', header: 'When', render: r => when(r.createdAt) },
  {
    key: 'type',
    header: 'Type',
    render: r => r.entry_type || <span style={{ color: palette.slateLight }}>—</span>,
  },
  { key: 'building', header: 'Building', render: r => r.property_name || '—' },
  { key: 'org', header: 'Organization', wide: true, render: r => r.org_name || '—' },
  { key: 'author', header: 'Recorded by', wide: true, render: r => r.author_name || '—' },
  {
    key: 'closed',
    header: 'Closed',
    render: r =>
      r.departed_at || r.closed_at ? (
        <Tag tone="neutral">{when(r.departed_at || r.closed_at)}</Tag>
      ) : (
        <Tag tone="success">open</Tag>
      ),
  },
];

export const PACKAGE_COLUMNS: Column<Row>[] = [
  { key: 'carrier', header: 'Carrier', render: r => r.carrier || '—' },
  { key: 'unit', header: 'For unit', render: r => r.unit_label || '—' },
  { key: 'building', header: 'Building', render: r => r.property_name || '—' },
  { key: 'org', header: 'Organization', wide: true, render: r => r.org_name || '—' },
  { key: 'status', header: 'Status', render: r => <StatusTag value={r.status} /> },
  { key: 'logged', header: 'Logged', render: r => when(r.logged_at) },
  {
    key: 'age',
    header: 'Age',
    numeric: true,
    render: r =>
      r.picked_up_at || r.returned_at ? (
        '—'
      ) : (
        <span style={{ color: Number(r.age_days) > 14 ? palette.alert : undefined }}>
          {n(r.age_days)}d
        </span>
      ),
  },
];

/* VISITOR PASSES (resident-portal Plan 3, RP-3.8). SHAPE ONLY: the API never
   returns a visitor's name, company, the note to the desk or the host, so there
   is nothing here to leak. The state is derived server-side in the building's
   own today; "off" means the building does not take passes. */
const PASS_STATE_TONE: Record<string, 'success' | 'warning' | 'neutral'> = {
  active: 'success',
  paused: 'warning',
  scheduled: 'warning',
  ended: 'neutral',
  off: 'neutral',
  pending: 'warning',
};
const passWhen = (r: Row): string => {
  const window =
    r.window_start && r.window_end ? `${r.window_start}–${r.window_end}` : 'any time';
  if (r.schedule_kind === 'once') return `Once, ${r.valid_from || '—'}, ${window}`;
  const every =
    Number(r.interval_weeks) > 1 ? `every ${r.interval_weeks} weeks` : 'weekly';

  return `${every}, ${window}, ${r.valid_from || '—'} to ${r.valid_until || '—'}`;
};

export const VISITOR_PASS_COLUMNS: Column<Row>[] = [
  { key: 'kind', header: 'Kind', render: r => labelFor(r.category) },
  { key: 'schedule', header: 'Schedule', wide: true, render: r => passWhen(r) },
  { key: 'unit', header: 'Unit', render: r => r.unit_label || '—' },
  { key: 'building', header: 'Building', render: r => r.property_name || '—' },
  { key: 'org', header: 'Organization', wide: true, render: r => r.org_name || '—' },
  {
    key: 'via',
    header: 'Added by',
    render: r => (r.created_via === 'resident' ? 'Resident' : 'Building'),
  },
  {
    key: 'state',
    header: 'State',
    render: r => (
      <StatusTag value={r.state} tone={PASS_STATE_TONE[String(r.state)] || 'neutral'} />
    ),
  },
  { key: 'arrivals', header: 'Arrivals', numeric: true, render: r => num(r.arrivals) },
  /* Plan 6 (126): a yes/no only — whether the household is sharing the pass as
     a link right now. The API never returns the link or its hash. */
  { key: 'shared', header: 'Shared', render: r => (r.shared ? 'Yes' : 'No') },
  { key: 'created', header: 'Added', render: r => when(r.created_at) },
];

/* SHORT STAYS (Lobbero Stays plan S3.11). SHAPE ONLY, like the passes above:
   the API returns no guest name, email, phone or note, so this table has
   nothing to leak. The check-in state is derived server-side from the stay's
   link and the guests' ID checks. The dates are building calendar days and are
   printed as they are, never re-zoned. */
const STAY_STATUS_TONE: Record<string, 'success' | 'warning' | 'neutral' | 'brand'> = {
  confirmed: 'brand',
  checked_in: 'success',
  checked_out: 'neutral',
  cancelled: 'neutral',
  no_show: 'warning',
};
const STAY_CHECKIN_TONE: Record<string, 'success' | 'warning' | 'neutral'> = {
  not_started: 'neutral',
  in_progress: 'warning',
  verified: 'success',
  incomplete: 'warning',
};
const stayDay = (value: unknown): string =>
  isDayKey(value)
    ? formatDayKey(value, { day: 'numeric', month: 'short', year: 'numeric' })
    : String(value || '—');

export const STAY_RESERVATION_COLUMNS: Column<Row>[] = [
  { key: 'org', header: 'Organisation', wide: true, render: r => r.org_name || '—' },
  { key: 'building', header: 'Building', render: r => r.property_name || '—' },
  { key: 'unit', header: 'Unit', render: r => r.unit_label || '—' },
  {
    key: 'dates',
    header: 'Dates',
    wide: true,
    render: r => `${stayDay(r.check_in)} → ${stayDay(r.check_out)}`,
  },
  { key: 'nights', header: 'Nights', numeric: true, render: r => num(r.nights) },
  { key: 'party', header: 'Party', numeric: true, render: r => num(r.party_size) },
  {
    key: 'status',
    header: 'Status',
    render: r => (
      <StatusTag value={r.status} tone={STAY_STATUS_TONE[String(r.status)] || 'neutral'} />
    ),
  },
  {
    key: 'checkin',
    header: 'Check-in',
    render: r => (
      <StatusTag
        value={r.checkin_state}
        tone={STAY_CHECKIN_TONE[String(r.checkin_state)] || 'neutral'}
      />
    ),
  },
  { key: 'arrived', header: 'Arrived', render: r => (r.arrived ? 'Yes' : 'No') },
  { key: 'opens', header: 'Link opens', numeric: true, render: r => num(r.opened_count) },
  { key: 'created', header: 'Created', render: r => when(r.created_at) },
];

/**
 * THE SHORT-STAYS LIST. A ListScreen like every other Operations list; the
 * organisation and the building are read from the address (`?org=`,
 * `?building=`) so a link from an organisation or a building lands on its own
 * stays. Status and check-in are the filter bar.
 */
export const StaysReservationsScreen: React.FC = () => {
  const [urlParams] = useSearchParams();
  const org = urlParams.get('org') || '';
  const building = urlParams.get('building') || '';
  const extraParams: Record<string, string> = {
    ...(org ? { org } : {}),
    ...(building ? { building } : {}),
  };

  return (
    <ListScreen
      resource="stays/reservations"
      columns={STAY_RESERVATION_COLUMNS}
      searchable
      searchPlaceholder="Status, check-in, unit, building, organisation…"
      extraParams={extraParams}
      filters={[
        {
          name: 'status',
          label: 'Status',
          options: ['confirmed', 'cancelled', 'no_show', 'checked_in', 'checked_out'].map(v => ({
            value: v,
            label: labelFor(v),
          })),
        },
        {
          name: 'checkin',
          label: 'Check-in',
          options: ['not_started', 'in_progress', 'verified', 'incomplete'].map(v => ({
            value: v,
            label: labelFor(v),
          })),
        },
      ]}
      note={
        <NoteBox>
          Stay shape only: the unit, the dates, the party size, the status, how far the
          online check-in got, whether the guest arrived and how often the check-in link
          was opened. Guest names, contact details and notes are not returned.
        </NoteBox>
      }
    />
  );
};

/* LOBBERO STAYS SIGN-UPS (Independent Stays IS3). Every account the public
   Stays sign-up made — read from the product history, no codes (sign-up is
   open to anyone). The owner's name and address are shown for support, like
   the users list. */
const SIGNUP_STATE_TONE: Record<string, 'success' | 'warning' | 'neutral'> = {
  live: 'success',
  no_live_listing: 'warning',
  no_property: 'neutral',
};
const SIGNUP_STATE_LABEL: Record<string, string> = {
  live: 'Listing live',
  no_live_listing: 'No live listing',
  no_property: 'No property yet',
};

export const STAYS_SIGNUP_COLUMNS: Column<Row>[] = [
  { key: 'org', header: 'Organisation', wide: true, render: r => r.org_name || <Id value={r.organization_id} /> },
  {
    key: 'owner',
    header: 'Owner',
    wide: true,
    render: r => (
      <>
        {r.owner_name || '—'}
        {r.owner_email ? <div style={{ color: palette.slate }}>{String(r.owner_email)}</div> : null}
      </>
    ),
  },
  { key: 'signed', header: 'Signed up', render: r => when(r.signed_up_at) },
  { key: 'verified', header: 'Email verified', render: r => (r.email_verified ? 'Yes' : 'No') },
  { key: 'properties', header: 'Properties', numeric: true, render: r => num(r.properties) },
  { key: 'listings', header: 'Live listings', numeric: true, render: r => num(r.live_listings) },
  {
    key: 'state',
    header: 'Where they are',
    render: r => (
      <Tag tone={SIGNUP_STATE_TONE[String(r.state)] || 'neutral'}>
        {SIGNUP_STATE_LABEL[String(r.state)] || String(r.state || '—')}
      </Tag>
    ),
  },
  { key: 'products', header: 'Products', render: r => (r.desk ? (r.stays ? 'Desk + Stays' : 'Desk') : 'Stays only') },
  {
    key: 'legal',
    header: 'Agreement',
    render: r => `Terms ${r.terms_version || '—'} · DPA ${r.dpa_version || '—'}`,
  },
];

export const StaysSignupsScreen: React.FC = () => (
  <ListScreen
    resource="stays/signups"
    columns={STAYS_SIGNUP_COLUMNS}
    searchable
    searchPlaceholder="Organisation, owner, email…"
    filters={[
      {
        name: 'state',
        label: 'Where they are',
        options: ['no_property', 'no_live_listing', 'live'].map(v => ({ value: v, label: SIGNUP_STATE_LABEL[v] })),
      },
    ]}
    note={
      <NoteBox>
        Accounts made by the public Lobbero Stays sign-up (closed until launch). Each one
        is Stays only until its owner adds Lobbero Desk; the agreement column names the
        terms and data processing agreement versions the owner accepted.
      </NoteBox>
    }
  />
);

/* INDEPENDENT ACCOUNTS (Independent Stays IS8.4). Every account that holds
   Lobbero Stays without Lobbero Desk — the ones that can download everything
   and close themselves. A row opens the organisation page. Counts only, plus
   the owner's contact for support. */
const ACCOUNT_STATE_TONE: Record<string, Tone> = {
  active: 'success',
  suspended: 'warning',
  frozen: 'warning',
  removing: 'neutral',
  failed: 'alert',
};
const ACCOUNT_STATE_LABEL: Record<string, string> = {
  active: 'Active',
  suspended: 'Suspended',
  frozen: 'Closing (frozen)',
  removing: 'Being removed',
  failed: 'Removal failed',
};

export const STAYS_ACCOUNT_COLUMNS: Column<Row>[] = [
  { key: 'org', header: 'Organisation', wide: true, render: r => r.org_name || <Id value={r.organization_id} /> },
  {
    key: 'owner',
    header: 'Owner',
    wide: true,
    render: r => (
      <>
        {r.owner_name || '—'}
        {r.owner_email ? <div style={{ color: palette.slate }}>{String(r.owner_email)}</div> : null}
      </>
    ),
  },
  { key: 'since', header: 'Stays since', render: r => when(r.stays_since || r.created_at) },
  { key: 'source', header: 'How', render: r => (r.stays_source === 'signup' ? 'Sign-up' : String(r.stays_source || '—')) },
  { key: 'plan', header: 'Plan', render: r => (r.stays_plan ? <Mono>{String(r.stays_plan)}</Mono> : 'Preview') },
  { key: 'properties', header: 'Properties', numeric: true, render: r => num(r.properties) },
  { key: 'listings', header: 'Live listings', numeric: true, render: r => num(r.live_listings) },
  { key: 'month', header: 'Stays this month', numeric: true, render: r => num(r.stays_this_month) },
  {
    key: 'state',
    header: 'State',
    render: r => (
      <>
        <Tag tone={ACCOUNT_STATE_TONE[String(r.state)] || 'neutral'}>
          {ACCOUNT_STATE_LABEL[String(r.state)] || String(r.state || '—')}
        </Tag>
        {r.freeze_until ? <div style={{ color: palette.slate }}>until {when(r.freeze_until)}</div> : null}
      </>
    ),
  },
];

export const StaysAccountsScreen: React.FC = () => (
  <ListScreen
    resource="stays/accounts"
    columns={STAYS_ACCOUNT_COLUMNS}
    rowKey={r => String(r.organization_id)}
    searchable
    searchPlaceholder="Organisation, owner, email…"
    onRowClick={(r, nav) => nav(`/admin/organizations/${encodeURIComponent(String(r.organization_id))}`)}
    filters={[
      {
        name: 'state',
        label: 'State',
        options: Object.keys(ACCOUNT_STATE_LABEL).map(v => ({ value: v, label: ACCOUNT_STATE_LABEL[v] })),
      },
    ]}
    emptyLabel="No account holds Lobbero Stays without Lobbero Desk."
    note={
      <NoteBox>
        Accounts with Lobbero Stays and no Lobbero Desk. Their owners can download everything and close the
        account themselves; a closed account is frozen for its grace period, then removed by the removal timer.
      </NoteBox>
    }
  />
);

/* THE CLOSURE QUEUE (Independent Stays IS8.4). Every close an owner made,
   newest first. A FAILED removal can be retried (elevated, with a reason): it
   goes back to the removal timer's queue; nothing is removed from here. */
const CLOSURE_TONE: Record<string, Tone> = {
  frozen: 'warning',
  reopened: 'success',
  removing: 'neutral',
  removed: 'neutral',
  failed: 'alert',
};
const CLOSURE_LABEL: Record<string, string> = {
  frozen: 'Frozen',
  reopened: 'Reopened',
  removing: 'Waiting for removal',
  removed: 'Removed',
  failed: 'Removal failed',
};

export const StaysClosuresScreen: React.FC<{ requestAction: (spec: ConfirmSpec) => void }> = ({ requestAction }) => {
  const [status, setStatus] = useState('');
  const { data, loading, error, reload } = useAdminResource<Row>(
    `/stays/closures?limit=100${status ? `&status=${encodeURIComponent(status)}` : ''}`,
  );
  const retry = (r: Row) =>
    requestAction({
      title: `Retry the removal of ${r.org_name || r.organization_id}`,
      body: (
        <>
          The closure goes back to the removal timer, which tries again on its next run. Fix the reason first (shown
          in the list): a retry of the same fault fails the same way.
        </>
      ),
      confirmLabel: 'Retry removal',
      danger: false,
      onConfirm: async reason => {
        await api.retryAccountClosure(String(r.id), reason);
        toast.success('Sent back to the removal timer');
        reload();
      },
    });
  const columns: Column<Row>[] = [
    { key: 'org', header: 'Organisation', wide: true, render: r => r.org_name || <Id value={r.organization_id} /> },
    { key: 'asked', header: 'Closed', render: r => when(r.requested_at) },
    { key: 'until', header: 'Frozen until', render: r => when(r.freeze_until) },
    {
      key: 'status',
      header: 'State',
      render: r => (
        <>
          <Tag tone={CLOSURE_TONE[String(r.status)] || 'neutral'}>
            {CLOSURE_LABEL[String(r.status)] || String(r.status || '—')}
          </Tag>
          {r.overdue ? <div style={{ color: palette.slate }}>past its day</div> : null}
        </>
      ),
    },
    { key: 'attempts', header: 'Attempts', numeric: true, render: r => num(r.attempts) },
    {
      key: 'outcome',
      header: 'Outcome',
      wide: true,
      render: r =>
        r.status === 'removed'
          ? `${when(r.removed_at)} · ${num(r.removed_rows)} rows`
          : r.last_error
            ? String(r.last_error)
            : '—',
    },
    {
      key: 'act',
      header: '',
      render: r => (r.status === 'failed' ? <Btn onClick={() => retry(r)}>Retry</Btn> : null),
    },
  ];
  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;

  return (
    <>
      <Toolbar>
        <Select aria-label="State" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All states</option>
          {Object.keys(CLOSURE_LABEL).map(v => (
            <option key={v} value={v}>
              {CLOSURE_LABEL[v]}
            </option>
          ))}
        </Select>
        <Btn onClick={reload}>Refresh</Btn>
      </Toolbar>
      <NoteBox>
        Owners of Stays-only accounts close them from Your data. The account is frozen for its grace period (the
        owner can reopen it), then the app hands it on and the removal timer on the app box removes it and records
        the counts. The last 100 are shown.
      </NoteBox>
      {loading || !data ? (
        <Loading />
      ) : (
        <AdminTable<Row>
          rows={(data.items as Row[]) || []}
          rowKey={r => String(r.id)}
          emptyLabel="No account has been closed."
          columns={columns}
        />
      )}
    </>
  );
};

/* Independent Stays IS4: every Stays billing account. Read-only, counts only. */
export const StaysBillingScreen: React.FC = () => {
  const { data, loading, error } = useAdminResource<Row>('/stays/billing');
  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (loading || !data) return <Loading />;

  return (
    <>
      <Panel title="Stays-only hosts on a plan">
        <AdminTable<Row>
          rows={(data.hosts as Row[]) || []}
          rowKey={r => String(r.organization_id)}
          emptyLabel="No Stays-only account is on a plan yet."
          columns={[
            { key: 'o', header: 'Organization', render: r => r.org_name || <Id value={r.organization_id} /> },
            { key: 'p', header: 'Plan', render: r => <Mono>{r.plan_code}</Mono> },
            { key: 's', header: 'Status', render: r => <StatusTag value={r.status} /> },
            { key: 'u', header: 'Places', numeric: true, render: r => String(r.unit_quantity ?? '—') },
            { key: 'c', header: 'Comped', render: r => (r.comped ? <Tag tone="neutral">comped</Tag> : '—') },
            { key: 't', header: 'Trial ends', wide: true, render: r => when(r.trial_end) },
          ]}
        />
      </Panel>
      <Panel title="Stays subscriptions">
        <AdminTable<Row>
          rows={(data.subscriptions as Row[]) || []}
          rowKey={r => `${r.organization_id}:${r.kind}:${r.status}`}
          emptyLabel="No Stays subscription yet."
          columns={[
            { key: 'o', header: 'Organization', render: r => r.org_name || <Id value={r.organization_id} /> },
            { key: 'k', header: 'Kind', render: r => (r.kind === 'addon' ? 'Listing slots' : 'Per check-in fee') },
            { key: 's', header: 'Status', render: r => <StatusTag value={r.status} /> },
            { key: 'q', header: 'Slots', numeric: true, render: r => (r.kind === 'addon' ? String(r.listing_quantity) : '—') },
            { key: 'p', header: 'Provider', render: r => (r.comped ? <Tag tone="neutral">comped</Tag> : r.provider) },
            {
              key: 'm',
              header: 'Meter',
              render: r =>
                Number(r.usage_expired) > 0 ? (
                  <Tag tone="alert">{`${r.usage_expired} expired`}</Tag>
                ) : Number(r.usage_pending) > 0 ? (
                  <Tag tone="warning">{`${r.usage_pending} pending`}</Tag>
                ) : (
                  '—'
                ),
            },
            { key: 'e', header: 'Period ends', wide: true, render: r => when(r.current_period_end) },
          ]}
        />
      </Panel>
    </>
  );
};

export const SHIFT_COLUMNS: Column<Row>[] = [
  { key: 'who', header: 'Person', render: r => r.full_name || <Id value={r.user_id} /> },
  { key: 'building', header: 'Building', render: r => r.property_name || '—' },
  { key: 'org', header: 'Organization', wide: true, render: r => r.org_name || '—' },
  { key: 'start', header: 'Started', render: r => when(r.started_at) },
  {
    key: 'end',
    header: 'Ended',
    render: r =>
      r.ended_at ? (
        <>
          {when(r.ended_at)}
          {r.ended_retroactively ? <Tag tone="warning">retro</Tag> : null}
        </>
      ) : (
        <Tag tone="success">open</Tag>
      ),
  },
  {
    key: 'hours',
    header: 'Hours',
    numeric: true,
    render: r => (
      <span
        style={{ color: !r.ended_at && Number(r.hours) > 16 ? palette.alert : undefined }}
      >
        {r.hours}
      </span>
    ),
  },
];

export const ACCESS_COLUMNS: Column<Row>[] = [
  { key: 'who', header: 'Person', render: r => r.full_name || <Id value={r.user_id} /> },
  {
    key: 'role',
    header: 'Role',
    render: r => (r.role ? <Tag tone="brand">{r.role.replace('_', ' ')}</Tag> : '—'),
  },
  { key: 'scope', header: 'Scope', render: r => r.scope_type },
  {
    key: 'target',
    header: 'Covers',
    render: r =>
      r.property_name ||
      r.portfolio_name ||
      (r.unit_id ? `unit ${r.unit_id}` : 'organization'),
  },
  { key: 'org', header: 'Organization', wide: true, render: r => r.org_name || '—' },
  {
    key: 'power',
    header: 'Power',
    render: r =>
      r.can_administer ? (
        <Tag tone="warning">administer</Tag>
      ) : r.can_operate ? (
        <Tag tone="neutral">operate</Tag>
      ) : (
        '—'
      ),
  },
  {
    key: 'valid',
    header: 'Valid',
    wide: true,
    render: r =>
      r.valid_until && new Date(r.valid_until) < new Date() ? (
        <Tag tone="alert">expired</Tag>
      ) : (
        <Tag tone="success">live</Tag>
      ),
  },
];

export const SESSION_COLUMNS: Column<Row>[] = [
  { key: 'who', header: 'Person', render: r => r.full_name || <Id value={r.user_id} /> },
  { key: 'org', header: 'Organization', wide: true, render: r => r.org_name || '—' },
  {
    key: 'device',
    header: 'Device',
    render: r => <span title={r.user_agent}>{shortUa(r.user_agent)}</span>,
  },
  { key: 'ip', header: 'IP', wide: true, render: r => <Mono>{r.ip || '—'}</Mono> },
  { key: 'last', header: 'Last used', render: r => when(r.last_used_at) },
  {
    key: 'state',
    header: 'State',
    render: r =>
      r.revoked_at ? (
        <Tag tone="alert">revoked</Tag>
      ) : new Date(r.expires_at) < new Date() ? (
        <Tag tone="neutral">expired</Tag>
      ) : (
        <Tag tone="success">live</Tag>
      ),
  },
];

/* A coarse device label. The full user agent is a fingerprint and sits in the
   title attribute; the column answers "does this sound like the person's own
   laptop", which a browser and a platform answer as well as 200 characters. */
const shortUa = (ua?: string): string => {
  const s = String(ua || '');
  if (!s) return 'unknown';
  const browser = /Edg\//.test(s)
    ? 'Edge'
    : /OPR\//.test(s)
      ? 'Opera'
      : /Chrome\//.test(s)
        ? 'Chrome'
        : /Safari\//.test(s)
          ? 'Safari'
          : /Firefox\//.test(s)
            ? 'Firefox'
            : 'browser';
  const os = /Windows/.test(s)
    ? 'Windows'
    : /Android/.test(s)
      ? 'Android'
      : /iPhone|iPad/.test(s)
        ? 'iOS'
        : /Mac OS X/.test(s)
          ? 'macOS'
          : /Linux/.test(s)
            ? 'Linux'
            : '';

  return os ? `${browser} · ${os}` : browser;
};

export const AI_COLUMNS: Column<Row>[] = [
  { key: 'when', header: 'When', render: r => when(r.created_at) },
  { key: 'feature', header: 'Feature', render: r => r.feature },
  {
    key: 'org',
    header: 'Organization',
    render: r => r.org_name || <Id value={r.organization_id} />,
  },
  { key: 'model', header: 'Model', wide: true, render: r => <Mono>{r.model}</Mono> },
  { key: 'status', header: 'Status', render: r => <StatusTag value={r.status} /> },
  {
    key: 'tokens',
    header: 'Tokens',
    numeric: true,
    wide: true,
    render: r => `${n(r.tokens_in)} / ${n(r.tokens_out)}`,
  },
  {
    key: 'cost',
    header: 'Cost',
    numeric: true,
    render: r => <Micros micros={Number(r.cost_micros || 0)} />,
  },
  {
    key: 'ms',
    header: 'Latency',
    numeric: true,
    render: r => (r.latency_ms ? `${n(r.latency_ms)}ms` : '—'),
  },
];

export const AUDIT_COLUMNS: Column<Row>[] = [
  // nowrap: a typed reason can run to a paragraph, and the table then gave the
  // date 58px — "13 Oct 2026, 14:22" down five lines.
  { key: 'when', header: 'When', nowrap: true, render: r => when(r.created_at) },
  {
    key: 'actor',
    header: 'Actor',
    render: r => r.actor_label || <Id value={r.actor_user_id} />,
  },
  { key: 'action', header: 'Action', render: r => <Mono>{r.action}</Mono> },
  {
    key: 'target',
    header: 'Target',
    render: r =>
      r.target_type ? `${r.target_type} ${String(r.target_id || '').slice(0, 12)}` : '—',
  },
  {
    key: 'org',
    header: 'Organization',
    wide: true,
    render: r => <Id value={r.target_org} />,
  },
  { key: 'reason', header: 'Reason', render: r => r.reason || '—' },
  {
    key: 'change',
    header: 'Change',
    wide: true,
    render: r =>
      r.before_state || r.after_state ? (
        <Mono>
          {JSON.stringify(r.before_state ?? {})} → {JSON.stringify(r.after_state ?? {})}
        </Mono>
      ) : (
        '—'
      ),
  },
  { key: 'ip', header: 'IP', wide: true, render: r => <Mono>{r.ip || '—'}</Mono> },
];

export const STRIPE_COLUMNS: Column<Row>[] = [
  { key: 'type', header: 'Event', render: r => <Mono>{r.type}</Mono> },
  { key: 'id', header: 'Event id', render: r => <Id value={r.id} /> },
  {
    key: 'object',
    header: 'Object',
    wide: true,
    render: r => <Id value={r.object_id} />,
  },
  {
    key: 'processed',
    header: 'Processed',
    render: r =>
      r.processed_at ? when(r.processed_at) : <Tag tone="warning">not processed</Tag>,
  },
];

export const PORTFOLIO_COLUMNS: Column<Row>[] = [
  { key: 'name', header: 'Portfolio', render: r => r.name },
  {
    key: 'org',
    header: 'Organization',
    render: r => r.org_name || <Id value={r.organization_id} />,
  },
  // THE ADMIN OF THE ORGANISATION, which is what "whose portfolio is this?"
  // actually means. `organizations.owner_user_id` is the org's admin by the
  // schema's own convention, decrypted server-side one row at a time.
  { key: 'owner', header: 'Owner', wide: true, render: r => r.owner_name || '—' },
  {
    key: 'ownermail',
    header: 'Owner email',
    wide: true,
    render: r => r.owner_email || '—',
  },
  { key: 'status', header: 'Status', render: r => <StatusTag value={r.status} /> },
  {
    key: 'default',
    header: 'Default',
    render: r => (r.is_default ? <Tag tone="brand">default</Tag> : '—'),
  },
  { key: 'buildings', header: 'Buildings', numeric: true, render: r => n(r.buildings) },
  { key: 'units', header: 'Units', numeric: true, render: r => n(r.units) },
  { key: 'created', header: 'Created', wide: true, render: r => when(r.createdAt) },
];

/* ═══════════════════════════════════════════════════════════════════════════
   SINGLE-PAYLOAD SCREENS
   ═══════════════════════════════════════════════════════════════════════════ */

const Panel: React.FC<{
  title: string;
  children: React.ReactNode;
  foot?: React.ReactNode;
}> = ({ title, children, foot }) => (
  <Card>
    <CardHead>
      <CardTitle>{title}</CardTitle>
    </CardHead>
    <CardBody>
      {children}
      {foot ? <div style={{ marginTop: spacing.sm }}>{foot}</div> : null}
    </CardBody>
  </Card>
);

const Facts: React.FC<{ rows: [string, React.ReactNode][] }> = ({ rows }) => (
  <DefList>
    {rows.map(([k, v]) => (
      <React.Fragment key={k}>
        <dt>{k}</dt>
        <dd>{v}</dd>
      </React.Fragment>
    ))}
  </DefList>
);

const Loading: React.FC = () => (
  <div aria-busy="true">
    {Array.from({ length: 5 }).map((_, i) => (
      /* eslint-disable-next-line react/no-array-index-key */
      <SkeletonRow key={i} />
    ))}
  </div>
);

export const PerformanceScreen: React.FC = () => {
  const { data, loading, error, reload } = useAdminResource<Row>('/performance');
  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (loading || !data) return <Loading />;

  const rt = data.runtime;
  const l5 = data.last_5m;
  const l60 = data.last_60m;
  const peak = Math.max(1, ...data.series.map((s: Row) => Number(s.requests)));

  return (
    <>
      <TileGrid>
        <Tile tone={l5.error_rate > 0.02 ? 'alert' : undefined}>
          <TileLabel>Errors, last 5 min</TileLabel>
          <TileValue>{(l5.error_rate * 100).toFixed(1)}%</TileValue>
          <TileFoot>
            {n(l5.errors)} of {n(l5.requests)}
          </TileFoot>
        </Tile>
        <Tile>
          <TileLabel>Requests / min</TileLabel>
          <TileValue>{l5.rpm}</TileValue>
          <TileFoot>{l60.rpm}/min over the hour</TileFoot>
        </Tile>
        <Tile>
          <TileLabel>Average latency</TileLabel>
          <TileValue>{n(l5.avg_ms)}ms</TileValue>
          <TileFoot>{n(l60.avg_ms)}ms over the hour</TileFoot>
        </Tile>
        <Tile tone={rt.event_loop_lag_ms > 100 ? 'warning' : undefined}>
          <TileLabel>Event-loop lag</TileLabel>
          <TileValue>{n(rt.event_loop_lag_ms)}ms</TileValue>
          <TileFoot>synchronous work blocking Node</TileFoot>
        </Tile>
        <Tile>
          <TileLabel>In flight</TileLabel>
          <TileValue>{n(data.in_flight)}</TileValue>
          <TileFoot>peak {n(data.peak_in_flight)}</TileFoot>
        </Tile>
        <Tile>
          <TileLabel>Memory (RSS)</TileLabel>
          <TileValue>{n(rt.rss_mb)}MB</TileValue>
          <TileFoot>
            heap {n(rt.heap_used_mb)}/{n(rt.heap_total_mb)}MB
          </TileFoot>
        </Tile>
        <Tile>
          <TileLabel>Uptime</TileLabel>
          <TileValue>{Math.floor(data.uptime_s / 3600)}h</TileValue>
          <TileFoot>since {when(data.since)}</TileFoot>
        </Tile>
      </TileGrid>

      <Panel title="Requests per minute, last hour">
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 90 }}>
          {data.series.map((s: Row) => (
            <div
              key={s.minute}
              title={`${when(s.minute)} — ${s.requests} requests, ${s.errors} errors, ${s.avg_ms}ms avg`}
              style={{
                flex: 1,
                minWidth: 2,
                height: `${(Number(s.requests) / peak) * 100}%`,
                background: Number(s.errors) ? palette.alert : palette.teal,
                borderRadius: 2,
                opacity: Number(s.requests) ? 1 : 0.15,
              }}
            />
          ))}
        </div>
      </Panel>

      <Panel title="By route" foot={<NoteBox>{data.note}</NoteBox>}>
        <AdminTable<Row>
          rows={data.routes}
          rowKey={r => r.route}
          columns={[
            { key: 'route', header: 'Route', render: r => <Mono>{r.route}</Mono> },
            { key: 'count', header: 'Calls', numeric: true, render: r => n(r.count) },
            {
              key: 'errors',
              header: '5xx',
              numeric: true,
              render: r =>
                Number(r.errors) ? (
                  <strong style={{ color: palette.alert }}>{n(r.errors)}</strong>
                ) : (
                  '0'
                ),
            },
            {
              key: 'refusals',
              header: '4xx',
              numeric: true,
              wide: true,
              render: r => n(r.refusals),
            },
            { key: 'p50', header: 'p50', numeric: true, render: r => `${r.p50_ms}ms` },
            { key: 'p95', header: 'p95', numeric: true, render: r => `${r.p95_ms}ms` },
            {
              key: 'p99',
              header: 'p99',
              numeric: true,
              wide: true,
              render: r => `${r.p99_ms}ms`,
            },
            {
              key: 'max',
              header: 'Max',
              numeric: true,
              wide: true,
              render: r => `${n(r.max_ms)}ms`,
            },
          ]}
        />
      </Panel>

      {data.recent_errors.length ? (
        <Panel title="Recent 5xx">
          <AdminTable<Row>
            rows={data.recent_errors}
            rowKey={r => `${r.at}${r.route}`}
            columns={[
              { key: 'at', header: 'When', render: r => when(r.at) },
              { key: 'route', header: 'Route', render: r => <Mono>{r.route}</Mono> },
              {
                key: 'status',
                header: 'Status',
                render: r => <Tag tone="alert">{r.status}</Tag>,
              },
              { key: 'ms', header: 'Took', numeric: true, render: r => `${n(r.ms)}ms` },
            ]}
          />
        </Panel>
      ) : null}

      <Toolbar>
        <Btn onClick={reload}>Refresh</Btn>
      </Toolbar>
    </>
  );
};

export const DatabaseScreen: React.FC = () => {
  const { data, loading, error, reload } = useAdminResource<Row>('/database');
  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (loading || !data) return <Loading />;
  const conn = data.connections;
  const usedPct = (Number(conn.total) / Math.max(1, Number(conn.max_connections))) * 100;

  return (
    <>
      <TileGrid>
        <Tile tone={usedPct > 80 ? 'alert' : usedPct > 60 ? 'warning' : undefined}>
          <TileLabel>Connections</TileLabel>
          <TileValue>
            {n(conn.total)}
            <span style={{ fontSize: 14, color: palette.slateLight }}>
              {' '}
              / {n(conn.max_connections)}
            </span>
          </TileValue>
          <TileFoot>
            <Bar pct={usedPct} tone={usedPct > 80 ? 'alert' : undefined} />
          </TileFoot>
        </Tile>
        <Tile tone={Number(conn.idle_in_transaction) > 3 ? 'warning' : undefined}>
          <TileLabel>Idle in transaction</TileLabel>
          <TileValue>{n(conn.idle_in_transaction)}</TileValue>
          <TileFoot>the pool-deadlock shape</TileFoot>
        </Tile>
        <Tile tone={Number(conn.waiting_on_lock) ? 'alert' : undefined}>
          <TileLabel>Waiting on a lock</TileLabel>
          <TileValue>{n(conn.waiting_on_lock)}</TileValue>
        </Tile>
        <Tile>
          <TileLabel>Cache hit</TileLabel>
          <TileValue>{data.cache.cache_hit_pct ?? '—'}%</TileValue>
          <TileFoot>{data.cache.size}</TileFoot>
        </Tile>
        <Tile tone={Number(data.cache.deadlocks) ? 'alert' : undefined}>
          <TileLabel>Deadlocks</TileLabel>
          <TileValue>{n(data.cache.deadlocks)}</TileValue>
          <TileFoot>{n(data.cache.rollbacks)} rollbacks</TileFoot>
        </Tile>
      </TileGrid>

      {data.long_running.length ? (
        <Panel
          title="Running longer than 5 seconds"
          foot={<NoteBox>{data.privacy}</NoteBox>}
        >
          <AdminTable<Row>
            rows={data.long_running}
            rowKey={r => String(r.pid)}
            columns={[
              { key: 'pid', header: 'PID', render: r => <Mono>{r.pid}</Mono> },
              {
                key: 'state',
                header: 'State',
                render: r => <StatusTag value={r.state} />,
              },
              { key: 'sec', header: 'Seconds', numeric: true, render: r => n(r.seconds) },
              {
                key: 'wait',
                header: 'Waiting on',
                render: r =>
                  r.wait_event ? `${r.wait_event_type}: ${r.wait_event}` : '—',
              },
              {
                key: 'app',
                header: 'Application',
                wide: true,
                render: r => r.application_name || '—',
              },
            ]}
          />
        </Panel>
      ) : (
        <Panel title="Long-running queries" foot={<NoteBox>{data.privacy}</NoteBox>}>
          <Empty>Nothing has been running for more than five seconds.</Empty>
        </Panel>
      )}

      <Panel title="Largest tables">
        <AdminTable<Row>
          rows={data.tables}
          rowKey={r => r.table}
          columns={[
            { key: 't', header: 'Table', render: r => <Mono>{r.table}</Mono> },
            {
              key: 'rows',
              header: 'Live rows',
              numeric: true,
              render: r => n(r.live_rows),
            },
            {
              key: 'dead',
              header: 'Dead rows',
              numeric: true,
              wide: true,
              render: r => n(r.dead_rows),
            },
            { key: 'size', header: 'Size', numeric: true, render: r => r.total_size },
            {
              key: 'seq',
              header: 'Seq scans',
              numeric: true,
              wide: true,
              render: r => n(r.seq_scan),
            },
            {
              key: 'idx',
              header: 'Index scans',
              numeric: true,
              wide: true,
              render: r => n(r.idx_scan),
            },
            {
              key: 'vac',
              header: 'Last autovacuum',
              wide: true,
              render: r => when(r.last_autovacuum),
            },
          ]}
        />
      </Panel>

      {data.unused_indexes.length ? (
        <Panel
          title="Indexes never used"
          foot={
            <NoteBox>
              An unused index still costs every write. Reported, not flagged — a seasonal
              query or a recently reset statistics counter both look like this.
            </NoteBox>
          }
        >
          <AdminTable<Row>
            rows={data.unused_indexes}
            rowKey={r => r.index}
            columns={[
              { key: 't', header: 'Table', render: r => <Mono>{r.table}</Mono> },
              { key: 'i', header: 'Index', render: r => <Mono>{r.index}</Mono> },
              { key: 's', header: 'Size', numeric: true, render: r => r.size },
            ]}
          />
        </Panel>
      ) : null}

      <Toolbar>
        <Btn onClick={reload}>Refresh</Btn>
      </Toolbar>
    </>
  );
};

export const HealthScreen: React.FC = () => {
  const { data, loading, error, reload } = useAdminResource<Row>('/health');
  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (loading || !data) return <Loading />;

  return (
    <>
      <TileGrid>
        <Tile tone={data.database.ok ? 'success' : 'alert'}>
          <TileLabel>Database</TileLabel>
          <TileValue>{data.database.ok ? 'up' : 'down'}</TileValue>
          <TileFoot>
            {n(data.database.latencyMs)}ms
            {data.database.reason ? ` · ${data.database.reason}` : ''}
          </TileFoot>
        </Tile>
        <Tile>
          <TileLabel>Pool</TileLabel>
          <TileValue>
            {n(data.pool.size)}
            <span style={{ fontSize: 14, color: palette.slateLight }}>
              {' '}
              / {n(data.pool.max)}
            </span>
          </TileValue>
          <TileFoot>
            {n(data.pool.available)} free · {n(data.pool.pending)} waiting
          </TileFoot>
        </Tile>
        <Tile>
          <TileLabel>Live sessions</TileLabel>
          <TileValue>{n(data.sessions.live)}</TileValue>
        </Tile>
        <Tile>
          <TileLabel>Schema</TileLabel>
          <TileValue>{n(data.schema.migrations)}</TileValue>
          <TileFoot>
            <Mono>{data.schema.latest}</Mono>
          </TileFoot>
        </Tile>
      </TileGrid>

      {/* Every outside service, one card each (ai-plans/integrations WP-10). The
          old presence table lived here; each card now opens the service's page. */}
      <IntegrationsIndex />

      <Panel title="Process">
        <Facts
          rows={[
            ['Node', <Mono key="n">{data.process.node}</Mono>],
            ['Environment', data.process.env],
            [
              'Uptime',
              `${Math.floor(data.process.uptime_s / 3600)}h ${Math.floor((data.process.uptime_s % 3600) / 60)}m`,
            ],
            ['Memory (RSS)', `${n(data.process.rss_mb)} MB`],
          ]}
        />
      </Panel>

      <Toolbar>
        <Btn onClick={reload}>Refresh</Btn>
      </Toolbar>
    </>
  );
};

export const UsageScreen: React.FC = () => {
  const [days, setDays] = useState('30');
  const { data, loading, error } = useAdminResource<Row>(`/usage?since=${days}`);
  const nav = useNavigate();

  return (
    <>
      <Toolbar>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: typography.size.xxs, color: palette.slate }}>
            Window
          </span>
          <Select value={days} onChange={e => setDays(e.target.value)}>
            <option value="7">7 days</option>
            <option value="30">30 days</option>
            <option value="90">90 days</option>
          </Select>
        </label>
      </Toolbar>

      {error ? <ErrorBox role="alert">{error}</ErrorBox> : null}
      {loading || !data ? (
        <Loading />
      ) : (
        <>
          <TileGrid>
            <Tile>
              <TileLabel>Units bought</TileLabel>
              <TileValue>{n(data.units.bought)}</TileValue>
            </Tile>
            <Tile
              tone={
                Number(data.units.used) > Number(data.units.bought) ? 'alert' : undefined
              }
            >
              <TileLabel>Units used</TileLabel>
              <TileValue>{n(data.units.used)}</TileValue>
              <TileFoot>
                <Bar
                  pct={
                    (Number(data.units.used) / Math.max(1, Number(data.units.bought))) *
                    100
                  }
                  tone={
                    Number(data.units.used) > Number(data.units.bought)
                      ? 'alert'
                      : undefined
                  }
                />
              </TileFoot>
            </Tile>
            <Tile>
              <TileLabel>Occupied</TileLabel>
              <TileValue>{n(data.units.occupied)}</TileValue>
            </Tile>
            <Tile>
              <TileLabel>Returned</TileLabel>
              <TileValue>{n(data.units.returned)}</TileValue>
              <TileFoot>{n(data.units.placeholders)} placeholders (free)</TileFoot>
            </Tile>
          </TileGrid>

          <Panel title="Biggest estates">
            <AdminTable<Row>
              rows={data.top_orgs}
              rowKey={r => r.id}
              onRowClick={r => nav(`/admin/organizations/${encodeURIComponent(r.id)}`)}
              columns={[
                {
                  key: 'n',
                  header: 'Organization',
                  render: r => r.name || <Id value={r.id} />,
                },
                { key: 'u', header: 'Used', numeric: true, render: r => n(r.used) },
                { key: 'b', header: 'Bought', numeric: true, render: r => n(r.bought) },
                {
                  key: 'p',
                  header: 'Consumed',
                  render: r => (
                    <Bar pct={(Number(r.used) / Math.max(1, Number(r.bought))) * 100} />
                  ),
                },
              ]}
            />
          </Panel>

          <Split>
            <Panel title="AI spend by organization">
              <AdminTable<Row>
                rows={data.iq_by_org}
                rowKey={r => String(r.organization_id)}
                emptyLabel="No AI calls in this window."
                columns={[
                  {
                    key: 'o',
                    header: 'Organization',
                    render: r => r.org_name || <Id value={r.organization_id} />,
                  },
                  { key: 'c', header: 'Calls', numeric: true, render: r => n(r.calls) },
                  { key: 'e', header: 'Errors', numeric: true, render: r => n(r.errors) },
                  {
                    key: 'm',
                    header: 'Cost',
                    numeric: true,
                    render: r => <Micros micros={Number(r.cost_micros)} />,
                  },
                ]}
              />
            </Panel>
            <Panel title="AI by feature">
              <AdminTable<Row>
                rows={data.iq_by_feature}
                rowKey={r => String(r.feature)}
                emptyLabel="No AI calls in this window."
                columns={[
                  { key: 'f', header: 'Feature', render: r => r.feature },
                  { key: 'c', header: 'Calls', numeric: true, render: r => n(r.calls) },
                  {
                    key: 'l',
                    header: 'Avg',
                    numeric: true,
                    render: r => `${n(r.avg_latency_ms)}ms`,
                  },
                  {
                    key: 'm',
                    header: 'Cost',
                    numeric: true,
                    render: r => <Micros micros={Number(r.cost_micros)} />,
                  },
                ]}
              />
            </Panel>
          </Split>

          <Panel title="Product activity">
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 100 }}>
              {(() => {
                const peak = Math.max(
                  1,
                  ...data.activity.map(
                    (d: Row) => Number(d.visitors) + Number(d.activities)
                  )
                );

                return data.activity.map((d: Row) => (
                  <div
                    key={String(d.day)}
                    title={`${dayLabel(d.day)} — ${d.visitors} visitors, ${d.activities} activities`}
                    style={{
                      flex: 1,
                      minWidth: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      height: '100%',
                    }}
                  >
                    <div
                      style={{
                        height: `${(Number(d.activities) / peak) * 100}%`,
                        background: palette.lagoon,
                        borderRadius: '2px 2px 0 0',
                      }}
                    />
                    <div
                      style={{
                        height: `${(Number(d.visitors) / peak) * 100}%`,
                        background: palette.teal,
                      }}
                    />
                  </div>
                ));
              })()}
            </div>
            <TileFoot>
              <span style={{ color: palette.teal }}>■</span> visitor entries{' '}
              <span style={{ color: palette.lagoon }}>■</span> building activities
              {/* The days are the READER's days (the server cuts them in `tz`),
                  or UTC's when the device zone did not arrive — said either way,
                  so a UTC day never passes for a local one. */}
              {' · '}
              {data.tz_source === 'viewer'
                ? `days on your time (${zoneLabel(data.tz) || data.tz})`
                : 'days in UTC'}
            </TileFoot>
          </Panel>
        </>
      )}
    </>
  );
};

export const IqScreen: React.FC = () => {
  const { data, loading, error } = useAdminResource<Row>('/iq?since=30');
  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (loading || !data) return <Loading />;

  return (
    <>
      <TileGrid>
        {/* THE STATE, NOT THE SWITCH. This tile used to read "KILL SWITCH on"
            while IQ was running: "on" described the feature, but the label
            named the switch, so the two readings are exact opposites and an
            operator mid-incident cannot tell which one is meant. Name the
            state the system is in and say what follows from it. */}
        <Tile tone={data.config?.enabled ? 'success' : 'warning'}>
          <TileLabel>Lobbero IQ</TileLabel>
          <TileValue>{data.config?.enabled ? 'Enabled' : 'Disabled'}</TileValue>
          <TileFoot>
            {data.config?.enabled
              ? `answering requests · provider ${data.config?.provider}`
              : 'kill switch engaged — all requests blocked'}
          </TileFoot>
        </Tile>
        <Tile>
          <TileLabel>Calls (30d)</TileLabel>
          <TileValue>{n(data.ledger.calls)}</TileValue>
          <TileFoot>{n(data.ledger.errors)} errors</TileFoot>
        </Tile>
        <Tile>
          <TileLabel>Spend (30d)</TileLabel>
          <TileValue>
            <Micros micros={Number(data.ledger.cost_micros)} />
          </TileValue>
          <TileFoot>{n(data.ledger.estimated_cost_rows)} estimated</TileFoot>
        </Tile>
        <Tile tone={Number(data.index.dead_letters) ? 'alert' : undefined}>
          <TileLabel>Embedding backlog</TileLabel>
          <TileValue>{n(data.index.pending)}</TileValue>
          <TileFoot>
            {n(data.index.dead_letters)} dead letters · {n(data.index.stale)} stale
          </TileFoot>
        </Tile>
        <Tile tone={Number(data.leases.expired) ? 'warning' : undefined}>
          <TileLabel>Open leases</TileLabel>
          <TileValue>{n(data.leases.open)}</TileValue>
          <TileFoot>{n(data.leases.expired)} expired</TileFoot>
        </Tile>
      </TileGrid>

      <Panel title="Configuration">
        <Facts
          rows={Object.entries(data.config || {}).map(([k, v]) => [
            k,
            typeof v === 'boolean' ? (
              v ? (
                'yes'
              ) : (
                'no'
              )
            ) : (
              <Mono key={k}>{String(v ?? '—')}</Mono>
            ),
          ])}
        />
      </Panel>

      {data.errors.length ? (
        <Panel title="Failure modes (30 days)">
          <AdminTable<Row>
            rows={data.errors}
            rowKey={r => `${r.error_code}${r.block_reason}${r.finish_reason}`}
            columns={[
              {
                key: 'e',
                header: 'Error code',
                render: r => <Mono>{r.error_code || '—'}</Mono>,
              },
              { key: 'b', header: 'Block reason', render: r => r.block_reason || '—' },
              { key: 'f', header: 'Finish reason', render: r => r.finish_reason || '—' },
              { key: 'n', header: 'Count', numeric: true, render: r => n(r.n) },
            ]}
          />
        </Panel>
      ) : null}

      <Panel title="Organizations entitled to IQ">
        <AdminTable<Row>
          rows={data.entitled_orgs}
          rowKey={r => String(r.organization_id)}
          emptyLabel="No organization has the AI layer enabled."
          columns={[
            {
              key: 'o',
              header: 'Organization',
              render: r => r.org_name || <Id value={r.organization_id} />,
            },
            {
              key: 's',
              header: 'Subscription',
              render: r => <StatusTag value={r.status} />,
            },
          ]}
        />
      </Panel>
    </>
  );
};

export const NotificationsScreen: React.FC = () => {
  const { data, loading, error } = useAdminResource<Row>('/notifications?since=30');
  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (loading || !data) return <Loading />;

  return (
    <>
      <TileGrid>
        <Tile>
          <TileLabel>Sent (30d)</TileLabel>
          <TileValue>{n(data.totals.n)}</TileValue>
        </Tile>
        <Tile tone={Number(data.totals.email_failed) ? 'alert' : undefined}>
          <TileLabel>Email failures</TileLabel>
          <TileValue>{n(data.totals.email_failed)}</TileValue>
          <TileFoot>{n(data.totals.email_queued)} still queued</TileFoot>
        </Tile>
        <Tile tone={Number(data.totals.push_failed) ? 'warning' : undefined}>
          <TileLabel>Push failures</TileLabel>
          <TileValue>{n(data.totals.push_failed)}</TileValue>
          <TileFoot>{n(data.totals.push_queued)} still queued</TileFoot>
        </Tile>
        <Tile>
          <TileLabel>Push devices</TileLabel>
          <TileValue>{n(data.devices.push_devices)}</TileValue>
          <TileFoot>{n(data.devices.push_users)} people</TileFoot>
        </Tile>
      </TileGrid>

      <Panel
        title="By event"
        foot={
          <NoteBox>
            Delivery state only. Notification titles and bodies name residents and units,
            so the console reports whether the machine works rather than what it said.
          </NoteBox>
        }
      >
        <AdminTable<Row>
          rows={data.by_event}
          rowKey={r => r.event_type}
          columns={[
            { key: 'e', header: 'Event', render: r => <Mono>{r.event_type}</Mono> },
            { key: 'n', header: 'Sent', numeric: true, render: r => n(r.n) },
            {
              key: 'es',
              header: 'Email ok',
              numeric: true,
              render: r => n(r.email_sent),
            },
            {
              key: 'ef',
              header: 'Email failed',
              numeric: true,
              render: r =>
                Number(r.email_failed) ? (
                  <strong style={{ color: palette.alert }}>{n(r.email_failed)}</strong>
                ) : (
                  '0'
                ),
            },
            {
              key: 'ps',
              header: 'Push ok',
              numeric: true,
              wide: true,
              render: r => n(r.push_sent),
            },
            {
              key: 'pf',
              header: 'Push failed',
              numeric: true,
              wide: true,
              render: r => n(r.push_failed),
            },
            {
              key: 'u',
              header: 'Unread',
              numeric: true,
              wide: true,
              render: r => n(r.unread),
            },
            {
              key: 's',
              header: 'Suppressed',
              numeric: true,
              wide: true,
              render: r => n(r.suppressed),
            },
          ]}
        />
      </Panel>

      <Panel title="Opt-outs by event">
        <AdminTable<Row>
          rows={data.opt_outs}
          rowKey={r => r.event_type}
          emptyLabel="Nobody has changed a notification preference."
          columns={[
            { key: 'e', header: 'Event', render: r => <Mono>{r.event_type}</Mono> },
            {
              key: 'a',
              header: 'In-app off',
              numeric: true,
              render: r => n(r.in_app_off),
            },
            { key: 'b', header: 'Email off', numeric: true, render: r => n(r.email_off) },
            { key: 'c', header: 'Push off', numeric: true, render: r => n(r.push_off) },
          ]}
        />
      </Panel>
    </>
  );
};

export const IntegrityScreen: React.FC = () => {
  const { data, loading, error } = useAdminResource<Row>('/integrity');
  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (loading || !data) return <Loading />;

  // EXPECTED COUNTS ARE NOT ANOMALIES. Everything in `anomalies` renders as a
  // warning tile when it is above zero, so a count that is legitimately
  // non-zero forever — co-residents the product lets a primary resident add
  // without a login — trained operators to read this screen as always-red.
  // It is reported below as a plain fact instead.
  const EXPECTED = new Set(['co_residents_without_login']);
  const anomalies = (Object.entries(data.anomalies) as [string, number][]).filter(
    ([k]) => !EXPECTED.has(k)
  );
  const bad = anomalies.filter(([, v]) => Number(v) > 0);
  const coResidents = Number(data.anomalies.co_residents_without_login ?? 0);

  return (
    <>
      <Panel
        title="Referential anomalies"
        foot={
          <NoteBox>
            None of these is caught by a foreign key — the columns are deliberately
            nullable. Each has been a real support ticket at least once.
          </NoteBox>
        }
      >
        {bad.length ? (
          <TileGrid>
            {bad.map(([k, v]) => (
              <Tile key={k} tone="warning">
                <TileLabel>{k.replace(/_/g, ' ')}</TileLabel>
                <TileValue>{n(v)}</TileValue>
              </Tile>
            ))}
          </TileGrid>
        ) : (
          <Empty>
            No anomalies. Every unit has a building, every occupancy has a unit.
          </Empty>
        )}
      </Panel>

      {/* Reported, not flagged: these are residents somebody chose not to
          invite, which is a supported thing to do. The number is worth
          knowing — it is how many people in occupied units cannot see their
          own building — and it is not a fault. */}
      <Panel title="Residents without a login">
        <Facts
          rows={[
            ['Co-residents with no account', n(coResidents)],
            [
              'Why this is not an anomaly',
              'A primary resident may add a co-resident without inviting them. Only a PRIMARY resident with no account counts as a broken reference above.',
            ],
          ]}
        />
      </Panel>

      <Split>
        <Panel title="Correction trail">
          <Facts
            rows={[
              [
                'Entry versions',
                `${n(data.entry_versions.rows)} across ${n(data.entry_versions.entries)} entries`,
              ],
              ['Latest version', when(data.entry_versions.latest)],
              [
                'Field edits',
                `${n(data.field_edits.rows)} across ${n(data.field_edits.entries)} entries`,
              ],
              ['Latest edit', when(data.field_edits.latest)],
            ]}
          />
        </Panel>
        <Panel title="Most corrections">
          <AdminTable<Row>
            rows={data.top_editors}
            rowKey={r => String(r.actor_user_id)}
            emptyLabel="Nobody has corrected an entry."
            columns={[
              {
                key: 'w',
                header: 'Person',
                render: r => r.full_name || <Id value={r.actor_user_id} />,
              },
              { key: 'n', header: 'Edits', numeric: true, render: r => n(r.edits) },
            ]}
          />
        </Panel>
      </Split>
    </>
  );
};

export const StorageScreen: React.FC = () => {
  const { data, loading, error } = useAdminResource<Row>('/storage');
  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (loading || !data) return <Loading />;

  return (
    <>
      <TileGrid>
        <Tile>
          <TileLabel>Objects</TileLabel>
          <TileValue>{n(data.totals.images)}</TileValue>
        </Tile>
        <Tile>
          <TileLabel>Stored</TileLabel>
          <TileValue>{bytes(data.totals.bytes)}</TileValue>
        </Tile>
        <Tile>
          <TileLabel>Organizations using it</TileLabel>
          <TileValue>{n(data.totals.orgs)}</TileValue>
        </Tile>
      </TileGrid>
      <Panel title="By organization">
        <AdminTable<Row>
          rows={data.by_org}
          rowKey={r => String(r.organization_id)}
          emptyLabel="Nothing stored yet."
          columns={[
            {
              key: 'o',
              header: 'Organization',
              render: r => r.org_name || <Id value={r.organization_id} />,
            },
            { key: 'i', header: 'Objects', numeric: true, render: r => n(r.images) },
            { key: 'b', header: 'Size', numeric: true, render: r => bytes(r.bytes) },
          ]}
        />
      </Panel>
      <Panel title="By kind">
        <AdminTable<Row>
          rows={data.by_type}
          rowKey={r => String(r.entity_type)}
          emptyLabel="Nothing stored yet."
          columns={[
            { key: 't', header: 'Attached to', render: r => r.entity_type },
            { key: 'i', header: 'Objects', numeric: true, render: r => n(r.images) },
            { key: 'b', header: 'Size', numeric: true, render: r => bytes(r.bytes) },
          ]}
        />
      </Panel>
    </>
  );
};

export const CredentialsScreen: React.FC = () => {
  const { data, loading, error } = useAdminResource<Row>('/credentials');
  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (loading || !data) return <Loading />;

  return (
    <>
      <TileGrid>
        <Tile>
          <TileLabel>Live password resets</TileLabel>
          <TileValue>{n(data.password_resets.live)}</TileValue>
          <TileFoot>{n(data.password_resets.used)} used all time</TileFoot>
        </Tile>
        <Tile tone={Number(data.password_resets.expired_unused) ? 'warning' : undefined}>
          <TileLabel>Expired unused</TileLabel>
          <TileValue>{n(data.password_resets.expired_unused)}</TileValue>
          <TileFoot>people who never clicked the link</TileFoot>
        </Tile>
        <Tile
          tone={
            Number(data.email_verification.unverified_accounts) ? 'warning' : undefined
          }
        >
          <TileLabel>Unverified accounts</TileLabel>
          <TileValue>{n(data.email_verification.unverified_accounts)}</TileValue>
          <TileFoot>{n(data.email_verification.live_verify_tokens)} live tokens</TileFoot>
        </Tile>
        <Tile>
          <TileLabel>Pending email changes</TileLabel>
          <TileValue>{n(data.email_verification.pending_email_changes)}</TileValue>
        </Tile>
        <Tile>
          <TileLabel>Live invitations</TileLabel>
          <TileValue>{n(data.invitations.live_invites)}</TileValue>
          <TileFoot>{n(data.invitations.expired_invites)} expired</TileFoot>
        </Tile>
      </TileGrid>
      <NoteBox>{data.privacy}</NoteBox>
    </>
  );
};

export const CataloguesScreen: React.FC = () => {
  const { data, loading, error } = useAdminResource<Row>('/catalogues');
  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (loading || !data) return <Loading />;

  return (
    <>
      <Panel title="Plans">
        <AdminTable<Row>
          rows={data.plans}
          rowKey={r => String(r.id)}
          columns={[
            { key: 'c', header: 'Code', render: r => <Mono>{r.code}</Mono> },
            { key: 'n', header: 'Name', render: r => r.name },
            // Independent Stays IS4: which product, and a Stays row's role.
            {
              key: 'pl',
              header: 'Product',
              render: r =>
                r.product_line === 'stays' ? (
                  <Tag tone="brand">{`Stays · ${r.plan_role || 'pool'}`}</Tag>
                ) : (
                  'Desk'
                ),
            },
            {
              key: 'p',
              header: 'Price',
              numeric: true,
              render: r => (
                <>
                  <Money cents={Number(r.price_cents || 0)} />
                  {r.price_confirmed === false ? <Tag tone="warning">placeholder</Tag> : null}
                </>
              ),
            },
            {
              key: 'iv',
              header: 'Per',
              render: r => (r.billing_interval === 'year' ? 'year' : 'month'),
            },
            {
              key: 'lg',
              header: 'Offered',
              render: r =>
                !r.is_active ? (
                  '—'
                ) : r.legacy ? (
                  <Tag tone="neutral">grandfathered</Tag>
                ) : (
                  <Tag tone="success">yes</Tag>
                ),
            },
            {
              key: 'u',
              header: 'Per unit',
              render: r => (r.is_per_unit ? <Tag tone="brand">yes</Tag> : '—'),
            },
            {
              key: 'iq',
              header: 'IQ',
              render: r => (r.iq_enabled ? <Tag tone="brand">yes</Tag> : '—'),
            },
            {
              key: 'a',
              header: 'Active',
              render: r =>
                r.is_active ? (
                  <Tag tone="success">yes</Tag>
                ) : (
                  <Tag tone="neutral">no</Tag>
                ),
            },
            {
              key: 's',
              header: 'Live subs',
              numeric: true,
              render: r => n(r.live_subscriptions),
            },
            {
              key: 't',
              header: 'All subs',
              numeric: true,
              wide: true,
              render: r => n(r.subscriptions),
            },
          ]}
        />
      </Panel>
      <Panel title="Staffing models">
        <AdminTable<Row>
          rows={data.staffing_models}
          rowKey={r => String(r.id)}
          columns={[
            { key: 'c', header: 'Code', render: r => <Mono>{r.code}</Mono> },
            { key: 'l', header: 'Label', render: r => r.label },
            { key: 'b', header: 'Buildings', numeric: true, render: r => n(r.buildings) },
          ]}
        />
      </Panel>
      <Split>
        <Panel title="Saved dashboard views">
          <AdminTable<Row>
            rows={data.saved_views}
            rowKey={r => String(r.id)}
            emptyLabel="Nobody has saved a view."
            columns={[
              { key: 'n', header: 'Name', render: r => r.name },
              { key: 'o', header: 'Organization', render: r => r.org_name || '—' },
              {
                key: 's',
                header: 'Shared',
                render: r => (r.is_shared ? <Tag tone="brand">shared</Tag> : 'private'),
              },
            ]}
          />
        </Panel>
        <Panel title="Custom plan enquiries">
          <AdminTable<Row>
            rows={data.custom_plan_requests}
            rowKey={r => String(r.id)}
            emptyLabel="No enquiries."
            columns={[
              { key: 'n', header: 'Contact', render: r => r.contact_name || '—' },
              { key: 'b', header: 'Building', render: r => r.building_name || '—' },
              { key: 'u', header: 'Units', numeric: true, render: r => n(r.units) },
              { key: 's', header: 'Status', render: r => <StatusTag value={r.status} /> },
            ]}
          />
        </Panel>
      </Split>
      <Panel title="Applied migrations">
        <AdminTable<Row>
          rows={data.migrations}
          rowKey={r => r.filename}
          columns={[
            { key: 'f', header: 'File', render: r => <Mono>{r.filename}</Mono> },
            { key: 'a', header: 'Applied', render: r => when(r.applied_at) },
          ]}
        />
      </Panel>
    </>
  );
};

export {
  Panel as AdminPanel,
  Facts as AdminFacts,
  Loading as AdminLoading,
  bytes as formatBytes,
  Money,
  Micros,
};
